import { InsertDriveFile, PictureAsPdf } from '@mui/icons-material';
import { CircularProgress, IconButton, Link, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useState } from 'react';
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { ReactComponent as LinkIcon } from '../../../assets/icons/expand-link.svg';
import XlsIcon from "../../../assets/images/excel.png";
import PptIcon from "../../../assets/images/powerpoint.png";
import WordIcon from "../../../assets/images/word.png";
import ZipIcon from "../../../assets/images/zip.png";
import GoogleDocsIcon from "../../../assets/images/google-docs.png";
import GoogleSheetsIcon from "../../../assets/images/google-sheets.png";
import GoogleSlidesIcon from "../../../assets/images/google-slides.png";
import DocSendIcon from "../../../assets/images/docsend.png";
import { s3Download } from '../../../helpers/s3';
import { CompanyFile } from '../../../types/files';

// Download queue manager to prevent browser blocking multiple downloads
class DownloadQueue {
    private queue: Array<() => Promise<void>> = [];
    private isProcessing = false;
    private readonly DELAY_MS = 1000;

    async add(downloadFn: () => Promise<void>) {
        this.queue.push(downloadFn);
        if (!this.isProcessing) {
            await this.process();
        }
    }

    private async process() {
        this.isProcessing = true;

        while (this.queue.length > 0) {
            const downloadFn = this.queue.shift();
            if (downloadFn) {
                try {
                    await downloadFn();
                    // Add delay between downloads to prevent browser blocking
                    if (this.queue.length > 0) {
                        await new Promise(resolve => setTimeout(resolve, this.DELAY_MS));
                    }
                } catch (error) {
                    console.error('Download failed:', error);
                }
            }
        }

        this.isProcessing = false;
    }
}

// Global download queue instance
const downloadQueue = new DownloadQueue();

// Link type enumeration
type LinkType = 
    | 'google-docs' 
    | 'google-sheets' 
    | 'google-slides' 
    | 'google-forms'
    | 'google-drive'
    | 'office365-word' 
    | 'office365-excel' 
    | 'office365-powerpoint'
    | 'sharepoint'
    | 'docsend'
    | null;

// Helper function to detect Google Drive link type
const detectGoogleLinkType = (url: string): LinkType => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        const pathname = urlObj.pathname.toLowerCase();

        if (!hostname.includes('google.com')) {
            return null;
        }

        // Check pathname first for specific document types (more specific)
        if (pathname.includes('/spreadsheet')) {
            return 'google-sheets';
        }
        if (pathname.includes('/presentation')) {
            return 'google-slides';
        }
        if (pathname.includes('/document')) {
            return 'google-docs';
        }
        if (pathname.includes('/form')) {
            return 'google-forms';
        }

        // Then check specific subdomains (less specific but still useful)
        if (hostname.includes('sheets.google.com')) {
            return 'google-sheets';
        }
        if (hostname.includes('slides.google.com')) {
            return 'google-slides';
        }
        if (hostname.includes('docs.google.com') && pathname.includes('/document')) {
            return 'google-docs';
        }
        if (hostname.includes('forms.google.com')) {
            return 'google-forms';
        }
        if (hostname.includes('drive.google.com')) {
            return 'google-drive';
        }

        return null;
    } catch {
        return null;
    }
};

// Helper function to detect Microsoft Office 365 link type
const detectOfficeLinkType = (url: string): LinkType => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        const pathname = urlObj.pathname.toLowerCase();

        if (!hostname.includes('office.com') && 
            !hostname.includes('sharepoint.com') && 
            !hostname.includes('live.com') &&
            !hostname.includes('onedrive.') &&
            !hostname.includes('1drv.ms')) {
            return null;
        }

        // Check for Word documents
        if (pathname.includes('/word/') || 
            pathname.includes('.docx') || 
            pathname.includes('.doc') ||
            hostname.includes('word.office.com')) {
            return 'office365-word';
        }

        // Check for Excel spreadsheets
        if (pathname.includes('/excel/') || 
            pathname.includes('.xlsx') || 
            pathname.includes('.xls') ||
            hostname.includes('excel.office.com')) {
            return 'office365-excel';
        }

        // Check for PowerPoint presentations
        if (pathname.includes('/powerpoint/') || 
            pathname.includes('.pptx') || 
            pathname.includes('.ppt') ||
            hostname.includes('powerpoint.office.com')) {
            return 'office365-powerpoint';
        }

        // Generic SharePoint/OneDrive
        if (hostname.includes('sharepoint.com') || 
            hostname.includes('onedrive.') ||
            hostname.includes('1drv.ms')) {
            return 'sharepoint';
        }

        return null;
    } catch {
        return null;
    }
};

// Helper function to detect DocSend link type
const detectDocSendLinkType = (url: string): LinkType => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();

        if (hostname.includes('docsend.com')) {
            return 'docsend';
        }

        return null;
    } catch {
        return null;
    }
};

// File type theme configuration
const FILE_THEMES = {
    pdf: {
        color: '#c62828',
        backgroundColor: '#ffebee',
        hoverBackgroundColor: '#ffcdd2',
    },
    word: {
        color: '#1565c0',
        backgroundColor: '#e3f2fd',
        hoverBackgroundColor: '#bbdefb',
    },
    excel: {
        color: '#2e7d32',
        backgroundColor: '#e8f5e9',
        hoverBackgroundColor: '#c8e6c9',
    },
    powerpoint: {
        color: '#d84315',
        backgroundColor: '#fbe9e7',
        hoverBackgroundColor: '#ffccbc',
    },
    archive: {
        color: '#616161',
        backgroundColor: '#f5f5f5',
        hoverBackgroundColor: '#eeeeee',
    },
    link: {
        color: '#0277bd',
        backgroundColor: '#e1f5fe',
        hoverBackgroundColor: '#b3e5fc',
    },
    'google-docs': {
        color: '#1a73e8',
        backgroundColor: '#e8f0fe',
        hoverBackgroundColor: '#d2e3fc',
    },
    'google-sheets': {
        color: '#0f9d58',
        backgroundColor: '#e6f4ea',
        hoverBackgroundColor: '#ceead6',
    },
    'google-slides': {
        color: '#f9ab00',
        backgroundColor: '#fef7e0',
        hoverBackgroundColor: '#feefc3',
    },
    'google-forms': {
        color: '#673ab7',
        backgroundColor: '#f3e5f5',
        hoverBackgroundColor: '#e1bee7',
    },
    'google-drive': {
        color: '#4285f4',
        backgroundColor: '#e8f0fe',
        hoverBackgroundColor: '#d2e3fc',
    },
    'office365-word': {
        color: '#2b579a',
        backgroundColor: '#e7effa',
        hoverBackgroundColor: '#cfe1f7',
    },
    'office365-excel': {
        color: '#217346',
        backgroundColor: '#e7f4ed',
        hoverBackgroundColor: '#cfe9db',
    },
    'office365-powerpoint': {
        color: '#b7472a',
        backgroundColor: '#f9ebe7',
        hoverBackgroundColor: '#f3d7cf',
    },
    'sharepoint': {
        color: '#0078d4',
        backgroundColor: '#e5f1fb',
        hoverBackgroundColor: '#cce4f7',
    },
    'docsend': {
        color: '#0066ff',
        backgroundColor: '#e6f0ff',
        hoverBackgroundColor: '#cce0ff',
    },
    default: {
        color: '#1976d2',
        backgroundColor: '#e3f2fd',
        hoverBackgroundColor: '#bbdefb',
    },
};

const useStyles = makeStyles((theme) => ({
    fileLink: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1),
        transition: 'background 0.2s',
        minWidth: 0,
    },
    iconWrapper: {
        padding: 6,
        border: '1px solid #e9ecef',
        borderRadius: 8,
        background: '#fff',
        "&:hover": {
            border: '1px solid #67acaf',
        }
    },
    donwloadIcon: {
        width: 18,
        height: 18,
        stroke: '#898d91',
        "&:hover": {
            stroke: '#67acaf',
        }
    },
    fileIcon: {
        width: 20,
        height: 20,
        objectFit: 'contain',
    },
    docSendIcon: {
        width: 23,
        height: 23,
        objectFit: 'contain',
    },
    fileName: {
        textDecoration: 'none',
        fontFamily: 'Lato',
        fontWeight: 600,
        fontSize: 14,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        whiteSpace: 'normal',
        textOverflow: 'ellipsis',
        flex: 1,
        minWidth: 0,
        "&:hover": {
            textDecoration: 'none !important',
        },
    },
}));

const mimeTypes: Record<string, string> = {
    // Word Documents
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'docm': 'application/vnd.ms-word.document.macroEnabled.12',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    'odt': 'application/vnd.oasis.opendocument.text',
    // Excel Spreadsheets
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
    'xltx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'csv': 'text/csv',
    'ods': 'application/vnd.oasis.opendocument.spreadsheet',
    // PowerPoint Presentations
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'pptm': 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    // Archives
    'zip': 'application/zip',
    'rar': 'application/vnd.rar',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
};

// Helper function to get file theme based on extension or link type
const getFileTheme = (fileName: string, isLink: boolean, linkType: LinkType = null) => {
    // Check for specific link types first
    if (isLink && linkType) {
        return FILE_THEMES[linkType] || FILE_THEMES.link;
    }

    if (isLink) {
        return FILE_THEMES.link;
    }

    const extension = fileName.toLowerCase().split('.').pop();

    switch (extension) {
        case 'pdf':
            return FILE_THEMES.pdf;
        case 'doc':
        case 'docx':
        case 'docm':
        case 'txt':
        case 'rtf':
        case 'odt':
            return FILE_THEMES.word;
        case 'xls':
        case 'xlsx':
        case 'xlsm':
        case 'xltx':
        case 'csv':
        case 'ods':
            return FILE_THEMES.excel;
        case 'ppt':
        case 'pptx':
        case 'pptm':
            return FILE_THEMES.powerpoint;
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
            return FILE_THEMES.archive;
        default:
            return FILE_THEMES.default;
    }
};

export const FileIconAtom: React.FC<{ fileName: string; isLink?: boolean; linkType?: LinkType; }> = ({ fileName, isLink, linkType }) => {
  const classes = useStyles();

   const icon = useMemo(() => {
    // Handle Google, Office 365, and DocSend link types
    if (isLink && linkType) {
      switch (linkType) {
        case 'google-docs':
          return <img src={GoogleDocsIcon} className={classes.fileIcon} alt="google-docs-icon" />;
        case 'google-sheets':
          return <img src={GoogleSheetsIcon} className={classes.fileIcon} alt="google-sheets-icon" />;
        case 'google-slides':
          return <img src={GoogleSlidesIcon} className={classes.fileIcon} alt="google-slides-icon" />;
        case 'google-forms':
          return <InsertDriveFile fontSize="small" sx={{ color: '#673ab7' }} />;
        case 'google-drive':
          return <InsertDriveFile fontSize="small" sx={{ color: '#4285f4' }} />;
        case 'office365-word':
          return <img src={WordIcon} className={classes.fileIcon} alt="word-online-icon" />;
        case 'office365-excel':
          return <img src={XlsIcon} className={classes.fileIcon} alt="excel-online-icon" />;
        case 'office365-powerpoint':
          return <img src={PptIcon} className={classes.fileIcon} alt="powerpoint-online-icon" />;
        case 'sharepoint':
          return <InsertDriveFile fontSize="small" sx={{ color: '#0078d4' }} />;
        case 'docsend':
          return <img src={DocSendIcon} className={classes.docSendIcon} alt="docsend-icon" />;
        default:
          return <LinkIcon style={{ width: 16, height: 16}} />;
      }
    }

    // Generic link without specific type
    if (isLink) return <LinkIcon style={{ width: 16, height: 16}} />;

    const extension = fileName.toLowerCase().split('.').pop();

    switch (extension) {
      // PDF (Red)
      case 'pdf':
        return <PictureAsPdf fontSize="small" sx={{ color: '#EA4335' }} />;
      // Word Documents (Blue)
      case 'doc':
      case 'docx':
      case 'docm':
      case 'txt':
      case 'rtf':
      case 'odt':
        return <img src={WordIcon} className={classes.fileIcon} alt="word-icon" />;
      // Excel Spreadsheets (Green)
      case 'xls':
      case 'xlsx':
      case 'xlsm':
      case 'xltx':
      case 'csv':
      case 'ods':
        return <img src={XlsIcon} className={classes.fileIcon} alt="excel-icon" />;
      // PowerPoint Presentations (Orange)
      case 'ppt':
      case 'pptx':
      case 'pptm':
        return <img src={PptIcon} className={classes.fileIcon} alt="powerpoint-icon" />;
      // Archives (Grey)
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <img src={ZipIcon} className={classes.fileIcon} alt="archive-icon" />;
      // Default
      default:
        return <InsertDriveFile fontSize="small" />;
    }
  // eslint-disable-next-line
  }, [fileName, isLink, linkType]);

  return icon;
};

const FileAttachment: React.FC<{
    resName: string;
    file?: CompanyFile;
}> = ({ resName, file }) => {
    const classes = useStyles();
    const [hover, setHover] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const downloadURL = useCallback((data: any, fileName: any) => {
        const a: any = document.createElement('a');
        a.href = data;
        a.download = fileName;
        document.body.appendChild(a);
        a.style = 'display: none';
        a.click();
        a.remove();
    }, []);

    const downloadBlob = useCallback((data: any, fileName: string, mimeType: string) => {
        const blob = new Blob([data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);

        downloadURL(url, fileName);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    }, [downloadURL]);

    const downloadFile = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (!!file?.key && !isDownloading) {
            setIsDownloading(true);

            // Add download to queue to prevent browser blocking multiple downloads
            downloadQueue.add(async () => {
                try {
                    const payload = await s3Download({ key: file.key });
                    downloadBlob(payload, file.name,
                        mimeTypes[file.name.toLowerCase().split('.').pop() || ''] || 'application/octet-stream');
                } finally {
                    setIsDownloading(false);
                }
            });
        }
        // eslint-disable-next-line
    }, [file, downloadBlob, isDownloading]);

    const fileName = resName;
    const isLink = ((str: string): boolean => {
        try {
            return ['http:', 'https:', 'ftp:', 'ftps:'].includes(new URL(str).protocol);
        } catch {
            return false;
        }
    })(resName);

    // Detect specific link type (Google Drive, Office 365, DocSend, etc.)
    const linkType = useMemo(() => {
        if (!isLink) return null;
        const googleType = detectGoogleLinkType(resName);
        if (googleType) return googleType;
        const officeType = detectOfficeLinkType(resName);
        if (officeType) return officeType;
        const docSendType = detectDocSendLinkType(resName);
        if (docSendType) return docSendType;
        return null;
    }, [isLink, resName]);

    // Get the appropriate theme for this file type
    const fileTheme = useMemo(() => getFileTheme(fileName, isLink, linkType), [fileName, isLink, linkType]);

	return (
        <Stack width="100%" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                 <Link
                    className={classes.fileLink}
                    href={isLink ? resName : '#'}
                    rel={isLink ? 'noopener noreferrer' : undefined}
                    target={isLink ? '_blank' : undefined}
                    underline="none"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: fileTheme.color,
                        backgroundColor: fileTheme.backgroundColor,
                        '&:hover': {
                            backgroundColor: fileTheme.hoverBackgroundColor,
                        },
                    }}
                >
                    <FileIconAtom fileName={fileName} isLink={isLink} linkType={linkType} />
                    <Typography className={classes.fileName}>{fileName}</Typography>
                </Link>
                {file && (hover || isDownloading) && (
                    <IconButton
                        onClick={downloadFile}
                        className={classes.iconWrapper}
                        disableRipple
                        disabled={isDownloading}
                        size="small"
                    >
                        {isDownloading ? (
                            <CircularProgress size={18} sx={{ color: '#67acaf' }} />
                        ) : (
                            <DownloadIcon className={classes.donwloadIcon} />
                        )}
                    </IconButton>
                )}
            </Stack>

        </Stack>
	);
};

export default FileAttachment;